import {
  Button,
  Dialog,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Hide, HStack, Stack, Text, VStack} from 'platform/foundation';
import type {z} from 'zod';

import {useFormState, useWatch} from 'react-hook-form';

import {TestIdProps, suffixTestId} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '../../../../../types/api/api';
import {getPresetTags} from '../utils/getPresetTags';
import {
  PresetEditorFormValues,
  buildPresetEditorDefaultValues,
  getRelativeFilterConfigFromPresetEditorForm,
  presetEditorFormSchema,
} from '../utils/presetEditorFormSchema';
import {BoundariesSection} from './BoundariesSection';
import {PropertiesSection} from './PropertiesSection';

interface PresetEditorDialogProps extends TestIdProps {
  isOpen: boolean;
  /** null means create mode */
  initialPreset: RelativeFilterResponseBody | null;
  onSave: (name: string, config: RelativeFilterConfigBody) => void;
  onDelete?: () => void;
  onClose: () => void;
}

type PresetEditorDialogFormBodyProps = PresetEditorDialogProps & {
  control: FormControl<PresetEditorFormValues>;
  isReadOnly: boolean;
};

const PRESET_SUMMARY_CONFIG_WATCH_NAMES = [
  'year',
  'mileage',
  'mileagePct',
  'power',
  'powerPct',
  'price',
  'pricePct',
  'featureScore',
  'trimLevel',
  'transmission',
  'fuel',
  'body',
  'drive',
  'vatDeductible',
  'sellerType',
  'seats',
  'radius',
] as const;

function PresetEditorDialogFormBody(props: PresetEditorDialogFormBodyProps) {
  const {control, isReadOnly} = props;

  const [
    wYear,
    wMileage,
    wMileagePct,
    wPower,
    wPowerPct,
    wPrice,
    wPricePct,
    wFeatureScore,
    wTrimLevel,
    wTransmission,
    wFuel,
    wBody,
    wDrive,
    wVatDeductible,
    wSellerType,
    wSeats,
    wRadius,
  ] = useWatch({
    control,
    name: [...PRESET_SUMMARY_CONFIG_WATCH_NAMES],
  });

  const summaryConfig: RelativeFilterConfigBody = {
    year: wYear ?? null,
    mileage: wMileage ?? null,
    mileagePct: wMileagePct ?? null,
    power: wPower ?? null,
    powerPct: wPowerPct ?? null,
    price: wPrice ?? null,
    pricePct: wPricePct ?? null,
    featureScore: wFeatureScore ?? null,
    trimLevel: wTrimLevel ?? null,
    transmission: wTransmission ?? null,
    fuel: wFuel ?? null,
    body: wBody ?? null,
    drive: wDrive ?? null,
    vatDeductible: wVatDeductible ?? null,
    sellerType: wSellerType ?? null,
    seats: wSeats ?? null,
    radius: wRadius ?? null,
  };

  const {isValid} = useFormState<PresetEditorFormValues>();
  const summaryTags = getPresetTags(summaryConfig);

  return (
    <>
      <VStack spacing={4}>
        <Stack direction={['column', 'row']} align="flex-start" justify="space-between" spacing={4}>
          <Box width={68} flexShrink={0}>
            <FormField
              control={control}
              name="name"
              type="text"
              label={i18n.t('entity.filterForm.savedFilters.dialog.presetName')}
              isRequired
              isDisabled={isReadOnly}
              data-testid={suffixTestId('nameInput', props)}
            />
          </Box>
          {summaryTags.length > 0 ? (
            <HStack spacing={1} justify={[undefined, 'flex-end']} wrap>
              {summaryTags.map((label, index) => (
                <Flag
                  // We do not have unique tags, so we use the index as the key
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}-${label}`}
                  label={label}
                  size="small"
                  colorScheme="blue"
                  isSubtle
                />
              ))}
            </HStack>
          ) : (
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.filterForm.savedFilters.summary.noConstraints')}
            </Text>
          )}
        </Stack>

        <Separator spacing={0} />

        <Stack direction={['column', 'row']} spacing={[4, 0]}>
          <Box flexGrow={1}>
            <BoundariesSection isReadOnly={isReadOnly} data-testid={props['data-testid']} />
          </Box>
          <Separator orientation="vertical" spacing={4} />
          <Box flexGrow={1}>
            <PropertiesSection isReadOnly={isReadOnly} />
          </Box>
        </Stack>
      </VStack>

      <DialogFooter data-testid={suffixTestId('footer', props)}>
        <HStack justify="space-between">
          <Hide when={isReadOnly || !props.onDelete}>
            <Button
              title={i18n.t('entity.filterForm.savedFilters.dialog.delete')}
              leftIcon="action/delete_outline"
              variant="dangerOutlined"
              type="button"
              onClick={props.onDelete}
              data-testid={suffixTestId('deleteButton', props)}
            />
          </Hide>
          <HStack spacing={2} justify="flex-end" grow={1}>
            {isReadOnly ? (
              <Button
                title={i18n.t('entity.filterForm.savedFilters.dialog.close')}
                variant="secondary"
                type="button"
                onClick={props.onClose}
                data-testid={suffixTestId('closeButton', props)}
              />
            ) : (
              <>
                <Button
                  title={i18n.t('entity.filterForm.savedFilters.dialog.cancel')}
                  variant="secondary"
                  type="button"
                  onClick={props.onClose}
                  data-testid={suffixTestId('cancelButton', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('entity.filterForm.savedFilters.dialog.save')}
                  isDisabled={!isValid}
                  data-testid={suffixTestId('saveButton', props)}
                />
              </>
            )}
          </HStack>
        </HStack>
      </DialogFooter>
    </>
  );
}

export function PresetEditorDialog(props: PresetEditorDialogProps) {
  const isSystem = props.initialPreset?.system ?? false;
  const isReadOnly = isSystem;

  const defaultValues = buildPresetEditorDefaultValues(props.initialPreset);

  const onSubmit: FormSubmitHandler<PresetEditorFormValues> = (values) => {
    props.onSave(values.name.trim(), getRelativeFilterConfigFromPresetEditorForm(values));
    return Promise.resolve();
  };

  return (
    <Dialog
      title={i18n.t('entity.filterForm.savedFilters.dialog.title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      withAdditionalFooter
      scrollBehavior="inside"
      data-testid={suffixTestId('dialog', props)}
    >
      <Form<PresetEditorFormValues>
        experimentalZodSchema={presetEditorFormSchema as z.ZodSchema<PresetEditorFormValues>}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isDisabled={isReadOnly}
        mode="onChange"
      >
        {(control) => (
          <PresetEditorDialogFormBody control={control} isReadOnly={isReadOnly} {...props} />
        )}
      </Form>
    </Dialog>
  );
}
